'use client';

import { useCallback, useMemo, useState } from 'react';

import { Check, Copy, FileText, Link2, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

import type { ChplQuote, ViewChplOptions } from '../types';
import type { NoInteraction } from '../types';

interface NoInteractionsBannerProps {
  safePairs: NoInteraction[];
  drugCount: number;
  totalPairs: number;
  /** Shareable URL for copying */
  shareableUrl?: string;
  /** Callback when user wants to view a ChPL document */
  onViewChpl?: (quote: ChplQuote, options?: ViewChplOptions) => void;
  /** Called when user clicks the copy-text button */
  onCopyText?: () => void;
  /** Whether text was just copied (shows confirmation) */
  textCopied?: boolean;
}

/**
 * Prominent green banner shown when no interactions are detected.
 * Replaces the summary card + collapsed safe pairs section.
 */
export function NoInteractionsBanner({
  safePairs,
  drugCount,
  totalPairs,
  shareableUrl,
  onViewChpl,
  onCopyText,
  textCopied = false,
}: NoInteractionsBannerProps) {
  const { t } = useTranslation('interactions');
  const [copied, setCopied] = useState(false);

  const handleCopyUrl = useCallback(async () => {
    if (!shareableUrl) return;

    try {
      await navigator.clipboard.writeText(shareableUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for browsers without clipboard API
      const input = document.createElement('input');
      input.value = shareableUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [shareableUrl]);

  // Get unique drugs from pairs
  const uniqueDrugs = useMemo(() => {
    const drugMap = new Map<string, (typeof safePairs)[0]['pair']['drug1']>();
    safePairs.forEach((pair) => {
      drugMap.set(pair.pair.drug1.id, pair.pair.drug1);
      drugMap.set(pair.pair.drug2.id, pair.pair.drug2);
    });
    return Array.from(drugMap.values());
  }, [safePairs]);

  const drugsWithChplPdf = useMemo(
    () => uniqueDrugs.filter((drug) => drug.hasChplPdf),
    [uniqueDrugs],
  );

  const handleDrugChplClick = useCallback(
    (drug: (typeof uniqueDrugs)[0]) => {
      if (!onViewChpl || !drug.hasChplPdf) return;
      onViewChpl(
        {
          drugId: drug.id,
          drugName: drug.productName,
          activeSubstance: drug.commonName,
          strength: drug.strength,
          form: drug.form,
          texts: [],
        },
        { scrollToChapter45: true },
      );
    },
    [onViewChpl],
  );

  return (
    <div>
      {/* Title bar */}
      <div className="mb-2">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-700 dark:text-zinc-200">
            {t('summary.title')}
          </h1>
          <div className="flex items-center gap-1">
            {shareableUrl && (
              <button
                type="button"
                onClick={handleCopyUrl}
                className={cn(
                  'flex items-center gap-1 rounded-md px-2 py-1 text-xs transition-colors',
                  copied
                    ? 'bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400'
                    : 'hover:bg-zinc-150 bg-zinc-100 text-zinc-400 hover:text-zinc-600 dark:bg-zinc-800/60 dark:text-zinc-500 dark:hover:bg-zinc-700/60 dark:hover:text-zinc-400',
                )}
              >
                {copied ? (
                  <>
                    <Check className="h-3 w-3" />
                    <span className="hidden @[640px]:inline">
                      {t('noInteractions.copiedLink')}
                    </span>
                  </>
                ) : (
                  <Link2 className="h-3 w-3" />
                )}
              </button>
            )}
            {onCopyText && (
              <button
                type="button"
                onClick={onCopyText}
                className={cn(
                  'flex items-center gap-1 rounded-md px-2 py-1 text-xs transition-colors',
                  textCopied
                    ? 'bg-green-50 text-green-600 dark:bg-green-900/30 dark:text-green-400'
                    : 'hover:bg-zinc-150 bg-zinc-100 text-zinc-400 hover:text-zinc-600 dark:bg-zinc-800/60 dark:text-zinc-500 dark:hover:bg-zinc-700/60 dark:hover:text-zinc-400',
                )}
              >
                {textCopied ? (
                  <>
                    <Check className="h-3 w-3" />
                    <span className="hidden @[640px]:inline">
                      {t('noInteractions.copiedResults')}
                    </span>
                  </>
                ) : (
                  <Copy className="h-3 w-3" />
                )}
              </button>
            )}
          </div>
        </div>
        <span className="text-xs text-zinc-500 dark:text-zinc-400">
          <span className="font-light">
            {t('drugsCount', { count: drugCount })}
          </span>
          <span className="mx-1 font-light">→</span>
          <span className="font-light">
            {t('pairsChecked', { count: totalPairs })}
          </span>
        </span>
      </div>

      {/* Green success banner */}
      <div className="rounded-xl border-2 border-green-200 bg-gradient-to-br from-green-50 to-emerald-50 p-5 @[640px]:p-8 dark:border-green-800 dark:from-green-950/40 dark:to-emerald-950/40">
        <div className="flex flex-col items-center text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-green-100 @[640px]:h-16 @[640px]:w-16 dark:bg-green-900/50">
            <ShieldCheck className="h-7 w-7 text-green-600 @[640px]:h-9 @[640px]:w-9 dark:text-green-400" />
          </div>
          <h2 className="mt-3 text-lg font-bold text-green-800 @[640px]:mt-4 @[640px]:text-xl dark:text-green-200">
            {t('noInteractions.title')}
          </h2>
          <p className="mt-2 text-sm text-green-700/80 dark:text-green-300/80">
            <Trans
              i18nKey="interactions:noInteractions.description"
              count={totalPairs}
              components={{
                short: <span className="@[640px]:hidden" />,
                long: <span className="hidden @[640px]:inline" />,
              }}
            />
          </p>

          {/* Checked pairs list */}
          <div className="mt-6 w-full max-w-lg space-y-2">
            {safePairs.map((pair, idx) => (
              <div
                key={idx}
                className="flex items-center gap-2 rounded-lg bg-white/60 px-3 py-2 text-sm dark:bg-green-900/20"
              >
                <Check className="h-4 w-4 shrink-0 text-green-500" />
                <span className="font-medium text-green-800 dark:text-green-300">
                  {pair.pair.drug1.productName}
                </span>
                <span className="text-green-500">+</span>
                <span className="font-medium text-green-800 dark:text-green-300">
                  {pair.pair.drug2.productName}
                </span>
              </div>
            ))}
          </div>

          {/* ChPL self-check section */}
          {onViewChpl && drugsWithChplPdf.length > 0 && (
            <div className="mt-14 w-full max-w-lg">
              <div className="text-xs text-green-700/70 dark:text-green-300/70">
                {t('noInteractions.verifyYourself')}
              </div>
              <div className="mt-0.5 mb-2 text-xs font-semibold text-green-800 dark:text-green-200">
                {t('noInteractions.readChapter')}
              </div>
              <div className="flex flex-wrap justify-center gap-2">
                {drugsWithChplPdf.map((drug) => (
                  <button
                    key={drug.id}
                    type="button"
                    onClick={() => handleDrugChplClick(drug)}
                    className="group inline-flex items-center gap-1.5 rounded-lg border-2 border-green-300 bg-white px-3 py-1.5 text-xs font-medium text-green-700 shadow-sm transition-all hover:border-green-400 hover:bg-green-50 hover:shadow-md dark:border-green-700 dark:bg-green-900/30 dark:text-green-300 dark:hover:border-green-600 dark:hover:bg-green-900/50"
                  >
                    <FileText className="h-3.5 w-3.5 transition-transform group-hover:scale-110" />
                    {drug.productName}
                  </button>
                ))}
              </div>
            </div>
          )}

          <p className="mt-10 text-xs leading-tight text-green-600/60 dark:text-green-400/60">
            {t('disclaimer.notSafe')} <br />
            {t('disclaimer.clinicalJudgement')}
          </p>
        </div>
      </div>
    </div>
  );
}
